import { useMemo, useEffect, useRef, useState } from 'react';
import styled from "styled-components";

import * as mq from "../../styles/media-queries.js";

import { connectComponent } from "../../store/store.js";
import {
	disableBodyScroll,
	enableBodyScroll,
} from "body-scroll-lock";

import LightboxImage from './LightboxImage.js';
import Swiper from "react-id-swiper";

import * as shared from "../../styles/shared.js";
import * as vars from "../../styles/variables.js";

import { Next, Prev } from '../NextPrev.js';

const Container = styled.section`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  z-index: 9;
  opacity: ${props => props.isVisible ? 1 : 0 };
  pointer-events: ${props => props.isVisible ? 'all' : 'none' };
  will-change: opacity;
  transition: opacity 0.22s ease;

  .swiper-container {
    width: 100%;
    height: 100%;
  }
`;

// const NextPrev = styled.button`
//   ${shared.buttonReset};
//   position: absolute;
//   top: 50%;
//   width: 1.5rem;
//   height: 1.5rem;
//   transform: translateY(-50%);
//   cursor: pointer;
//   z-index: 1;
//   transition: opacity 0.2s ease;
//   padding: 0;

//   &:before {
//     content: '';
//     position: absolute;
//     top: -50%;
//     left: -50%;
//     width: 200%;
//   height: 200%;
//   }

//   svg {
//     width: 100%;
//     height: auto;
//     pointer-events: bounding-box;
//   }

//   @media (hover: hover) {
//     &:hover {
//       polygon {
//         fill: ${vars.colours.accentRed} !important;
//       }
//     }
//   }
  
//   @media (hover: none) {
//     &:active {
//       polygon {
//         fill: ${vars.colours.accentRed} !important;
//       }
//     }
// 	}

//   &.swiper-button-disabled {
//     opacity: 0;
//     pointer-events: none;
//   }
// `;

// const Prev = styled(NextPrev)`
//   left: 15px;
// `;

// const Next = styled(NextPrev)`
//   right: 15px;
// `;

const Close = styled.button`
  ${shared.buttonReset};
  position: absolute;
  top: 1.2rem;
  right: 15px;
  width: 1rem;
  height: 1rem;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    top: -75%;
    left: -75%;
    width: 250%;
    height: 250%;
  }

  svg {
    width: 100%;
    height: auto;
    pointer-events: bounding-box;
  }

  @media (hover: hover) {
    &:hover {
      g {
        stroke: ${vars.colours.accentRed} !important;
      }
    }
  }
  
  @media (hover: none) {
    &:active {
      g {
        stroke: ${vars.colours.accentRed} !important;
      }
    }
	}
`;

const Lightbox = ({ articleHeader, articleContent, lightboxIndex, setLightboxIndex }) => {
  const [swiper, setSwiper] = useState(null);
  const [isVisible, setIsVisible] = useState(lightboxIndex !== null);
  const containerEl = useRef(null);
  const [uuid, setUuid] = useState(Math.round(Math.random() * 99999));

  const swiperOptions = {
		grabCursor: true,
    centeredSlides: true,
    speed: 555,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    navigation: {
      nextEl: `#lightbox-${uuid}__button--next`,
      prevEl: `#lightbox-${uuid}__button--prev`,
    },
    renderPrevButton: () => (
      <Next id={`lightbox-${uuid}__button--next`}>
        <svg
          width="47"
          height="47"
          viewBox="0 0 47 47"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g fill="none" fillRule="evenodd">
            <polygon
              points="23.482441 0 46.9648821 23.482441 23.482441 46.9648821 19.3627145 42.8451556 35.7042963 26.3662496 0 26.3662496 0 20.5986325 35.7042963 20.5986325 19.3627145 4.1197265"
              fill="#000"
              fillRule="nonzero"
            />
          </g>
        </svg>
      </Next>
    ),
    renderNextButton: () => (
      <Prev id={`lightbox-${uuid}__button--prev`}>
        <svg
          width="47"
          height="47"
          viewBox="0 0 47 47"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g fill="none" fillRule="evenodd">
            <polygon
              transform="matrix(-1 0 0 1 47.035 0)"
              points="23.5175591 0 47.0000001 23.482441 23.5175591 46.9648821 19.3978326 42.8451556 35.7394143 26.3662496 0.035118 26.3662496 0.035118 20.5986325 35.7394143 20.5986325 19.3978326 4.1197265"
              fill="#000"
              fillRule="nonzero"
            />
          </g>
        </svg>
      </Prev>
    )
  };
  
  const items = useMemo(() => {
    return [articleHeader, ...articleContent].reduce((items, block, index) => {
      if (block.type === 'image' || block.type === 'mediaBelow' || block.type === 'mediaSide' || block.type === 'mediaUnder') {
        items.push(<LightboxImage key={index} { ...block } />)
      } else if (block.type === 'gallery') {
        block.children.forEach((child, childIndex) => items.push(<LightboxImage key={`${index}_${childIndex}`} { ...child } />));
      } else if (block.type === 'galleryBelow') {
        block.gallery.forEach((child, childIndex) => items.push(<LightboxImage key={`${index}_${childIndex}`} { ...child } />));
      } else if (block.type === 'imageDiptych') {
        items.push(<LightboxImage key={`${index}_0`} image={block.imageOne} caption={block.captionOne} credit={caption.creditOne} altText={altTextOne} />);
        items.push(<LightboxImage key={`${index}_1`} image={block.imageTwo} caption={block.captionTwo} credit={caption.creditTwo} altText={altTextTwo} />);
      }
      return items;
    }, []);
  }, [articleContent, articleHeader]);

  useEffect(() => {
    if (lightboxIndex === null) {
      setIsVisible(false);
      enableBodyScroll(containerEl.current);
      return;
    }

    const onKeyDown = e => {
      if (e.key.toLowerCase() === 'escape') {
        setLightboxIndex(null);
      }
    }

    if (swiper && swiper.activeIndex !== lightboxIndex) {
      swiper.slideTo(lightboxIndex, 0);
    }
    requestAnimationFrame(() => setIsVisible(true));
    disableBodyScroll(containerEl.current);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxIndex]);

	return (
    <Container
      isVisible={isVisible}
      ref={containerEl}
    >
      <Swiper
        { ...swiperOptions }
        getSwiper={setSwiper}
        shouldSwiperUpdate={true}
        
      >
        {items}
      </Swiper>
      <Close onClick={() => setLightboxIndex(null)}>
        <svg
					width="10"
					height="11"
					viewBox="0 0 10 11"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g fill="none" fillRule="evenodd" strokeLinecap="square">
						<g
							transform="translate(-3 -2)"
							fillRule="nonzero"
							stroke="#000"
							strokeWidth="1.5"
						>
							<path
								d="M8,7.58333333 L12.1247896,3.45854378 L8,7.58333333 L3.87521044,3.45854378 L8,7.58333333 Z M8,7.58333333 L12.1247896,11.7081229 L8,7.58333333 L3.87521044,11.7081229 L8,7.58333333 Z"
								id="Combined-Shape"
							/>
						</g>
					</g>
				</svg>
      </Close>
      {/* SWIPER STYLES ARE BEING INCLUDED AS STATIC IMPORT */}
		</Container>
	);
};

export default connectComponent(Lightbox, ["ui.lightboxIndex"]);